// Avisos de custo e consumo (0.17.0): o que piorou em cada caminhão, para o
// Início. Conta pura, com os dados que o app já tem (litros, km do painel e
// despesas):
//   · consumo: os últimos abastecimentos (pelo menos 800 km) ficaram 15% ou
//     mais abaixo da média dos 90 dias antes deles (pelo menos 1.500 km);
//   · custo por km: o mês que acabou ficou 20% ou mais acima da média dos 3
//     meses antes dele, sem contar o motorista e o agenciamento (que
//     acompanham o frete, não os km).
import { diasEntre } from './datas.js';
import { abastecimentosEmOrdem, somarDias } from './despesas.js';
import { abastecimentosDoVeiculo, relatorioDosCaminhoes, ultimoDiaDoMes } from './custos.js';
import { dinheiro, mesCurto, mesPorExtenso, numeroBR, percentual, placa as formatarPlaca } from './formato.js';

export const CONSUMO = { kmRecente: 800, kmAntes: 1500, diasAntes: 90, queda: 0.15, recenteAte: 30 };
export const CUSTO = { alta: 0.2, minimoReais: 0.1, kmMinimo: 1000, mesesAntes: 3 };

const temKm = (a) => a.hodometro !== null && a.hodometro !== undefined && a.hodometro !== '';

// Consumo entre duas leituras (posições na lista): km ÷ litros colocados
// depois da primeira até a segunda (tanque cheio a tanque cheio).
function trecho(lista, i, j) {
  const km = Number(lista[j].hodometro) - Number(lista[i].hodometro);
  const litros = lista.slice(i + 1, j + 1).reduce((s, a) => s + Number(a.litros), 0);
  if (!(km > 0) || !(litros > 0)) return null;
  return { km, litros, kmPorLitro: km / litros, de: lista[i].data, ate: lista[j].data };
}

// O consumo caiu? Devolve { recente, anterior, queda } ou null.
export function consumoCaiu(abastecimentos, hoje, opcoes = {}) {
  const o = { ...CONSUMO, ...opcoes };
  const lista = abastecimentosEmOrdem(abastecimentos);
  const comKm = lista.map((a, i) => (temKm(a) ? i : -1)).filter((i) => i >= 0);
  if (comKm.length < 3) return null;
  const fim = comKm.at(-1);
  if (diasEntre(lista[fim].data, hoje) > o.recenteAte) return null;
  // Os últimos abastecimentos: volta até juntar os km mínimos
  let k = comKm.length - 2;
  while (k > 0 && Number(lista[fim].hodometro) - Number(lista[comKm[k]].hodometro) < o.kmRecente) k -= 1;
  const inicio = comKm[k];
  const recente = trecho(lista, inicio, fim);
  if (!recente || recente.km < o.kmRecente) return null;
  // Os 90 dias antes deles
  const limite = somarDias(lista[inicio].data, -o.diasAntes);
  const antes = comKm.filter((i) => i < inicio && lista[i].data >= limite);
  if (antes.length === 0) return null;
  const anterior = trecho(lista, antes[0], inicio);
  if (!anterior || anterior.km < o.kmAntes) return null;
  if (recente.kmPorLitro >= anterior.kmPorLitro * (1 - o.queda)) return null;
  return { recente, anterior, queda: 1 - recente.kmPorLitro / anterior.kmPorLitro };
}

// Custo por km de um mês do relatório, sem o motorista e o agenciamento
// (null quando o mês não tem os km completos).
export function custoOperacionalPorKm(mes) {
  if (mes.custoPorKm === null || mes.custoPorKm === undefined || !(mes.km?.km > 0)) return null;
  return (mes.custo - (mes.remuneracao?.total ?? 0) - (mes.agenciamentos ?? 0)) / mes.km.km;
}

// O custo por km subiu? `meses`: os do relatório (o último é o que acabou).
export function custoPorKmSubiu(meses, opcoes = {}) {
  const o = { ...CUSTO, ...opcoes };
  if (meses.length < 3) return null;
  const ultimo = meses.at(-1);
  const valor = custoOperacionalPorKm(ultimo);
  if (valor === null || ultimo.km.km < o.kmMinimo) return null;
  const antes = meses.slice(0, -1)
    .filter((m) => m.km?.km >= o.kmMinimo)
    .map((m) => ({ mes: m.mes, valor: custoOperacionalPorKm(m) }))
    .filter((m) => m.valor !== null && m.valor > 0);
  if (antes.length < 2) return null;
  const media = antes.reduce((s, m) => s + m.valor, 0) / antes.length;
  if (!(valor > media * (1 + o.alta)) || valor - media < o.minimoReais) return null;
  return { mes: ultimo.mes, valor, media, de: antes[0].mes, ate: antes.at(-1).mes, alta: valor / media - 1 };
}

// Mês anterior a 'AAAA-MM' (n meses antes).
function mesAntes(mes, n) {
  const [a, m] = mes.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1 - n, 1));
  return d.toISOString().slice(0, 7);
}

// Os avisos da frota: [{ tipo: 'consumo' | 'custo', veiculo, ... }], dos
// caminhões ativos. `tabelas`: as de dadosDosRelatorios(); `regraValendo` e
// `creditosDaViagem` de lib/acerto.js (o relatório precisa deles).
export function avisosDeCustoEConsumo(tabelas, { hoje, regraValendo, creditosDaViagem }) {
  const tratores = (tabelas.veiculos ?? []).filter((v) => v.tipo === 'trator' && v.ativo && !v.deleted_at);
  if (tratores.length === 0) return [];
  const avisos = [];
  const idsDiesel = new Set((tabelas.categorias ?? []).filter((c) => c.exige_hodometro).map((c) => c.id));
  const lancamentos = (tabelas.lancamentos ?? []).filter((l) => !l.deleted_at);
  for (const v of tratores) {
    const caiu = consumoCaiu(abastecimentosDoVeiculo(v.id, lancamentos, idsDiesel), hoje);
    if (caiu) avisos.push({ tipo: 'consumo', veiculo: v, ...caiu });
  }
  // O mês que acabou e os 3 antes dele
  const mesAtual = hoje.slice(0, 7);
  const ultimoMes = mesAntes(mesAtual, 1);
  const primeiro = mesAntes(mesAtual, 1 + CUSTO.mesesAntes);
  const relatorio = relatorioDosCaminhoes(tabelas, {
    de: `${primeiro}-01`, ate: ultimoDiaDoMes(ultimoMes), hoje, rateio: 'km', regraValendo, creditosDaViagem,
  });
  const ativos = new Set(tratores.map((v) => v.id));
  for (const c of relatorio.caminhoes) {
    if (!ativos.has(c.veiculo.id)) continue;
    const subiu = custoPorKmSubiu(c.meses);
    if (subiu) avisos.push({ tipo: 'custo', veiculo: tratores.find((v) => v.id === c.veiculo.id), ...subiu });
  }
  return avisos;
}

// O aviso em uma linha (o cartão do Início, o resumo da semana e o e-mail).
const nomeDoCaminhao = (v) => [v?.placa && formatarPlaca(v.placa), v?.apelido].filter(Boolean).join(' · ') || 'Caminhão';
const kmPorLitro = (v) => `${numeroBR(v, { casas: 2 })} km/l`;
export function textoDoAvisoDeCusto(a) {
  if (a.tipo === 'consumo') {
    return `${nomeDoCaminhao(a.veiculo)}: o consumo caiu para ${kmPorLitro(a.recente.kmPorLitro)} nos últimos ${numeroBR(a.recente.km)} km `
      + `(antes, ${kmPorLitro(a.anterior.kmPorLitro)}). Confira o hodômetro, os pneus, vazamentos e o jeito de dirigir.`;
  }
  return `${nomeDoCaminhao(a.veiculo)}: em ${mesPorExtenso(a.mes).split(' de ')[0]}, cada km custou ${dinheiro(a.valor)}, `
    + `${percentual(Math.round(a.alta * 100))} acima da média de ${mesCurto(a.de)} a ${mesCurto(a.ate)} (${dinheiro(a.media)}), sem o motorista e o agenciamento.`;
}
